import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  Switch,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBackIosNew as ArrowBackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useParams, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import Loader from 'src/common/Loader';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import NewVehicle from 'src/components/vehicles/NewVehicle';
import ROUTE from 'src/utils/routeConstants';
import {
  getVehicleDetails,
  updateVehicleDetails
} from 'src/redux/vehicleSlice';
import {
  setSuccessInfo,
  setErrorInfo
} from 'src/redux/modalSlice';
import {
  getFormattedDate,
  getMileageType,
  hasAdminLikeAccess
} from 'src/utils/getInitials';
import shouldDisplayDetailField from 'src/utils/detailFieldUtils';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import useAuth from 'src/context/AuthContext';

const formatLabel = (key) => key
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (typeof value === 'string') {
    const isDateLikeKey = /(_at|_on|_time|date)$/i.test(key);
    if (isDateLikeKey) {
      const formattedDate = getFormattedDate(value);
      if (formattedDate !== 'NA') return formattedDate;
    }
    return value;
  }

  if (typeof value === 'object') return JSON.stringify(value, null, 2);

  return value;
};

/* ================= PAGE ================= */

const VehicleDetails = () => {
  const theme = useTheme();
  const { vehicleId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const hasAdminAccess = hasAdminLikeAccess(getUser);

  const { loading, vehicleDetails } = useSelector(
    (state) => state.vehicles
  );

  const [openModal, setOpenModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    dispatch(getVehicleDetails(vehicleId)).unwrap();
  }, [dispatch, vehicleId]);
  const hasUnitId = vehicleDetails?.unit_id !== null
    && vehicleDetails?.unit_id !== undefined
    && String(vehicleDetails?.unit_id).trim() !== '';
  const canOpenFleetStatistics = Boolean(
    vehicleDetails?.telemetics_enabled === true
    && hasUnitId
  );

  const toggleStatus = async () => {
    setShowConfirm(false);
    setUpdating(true);
    try {
      await dispatch(
        updateVehicleDetails({
          ...vehicleDetails,
          active: !vehicleDetails.active
        })
      ).unwrap();
      dispatch(setSuccessInfo({ successMsg: 'Vehicle status updated' }));
    } catch (err) {
      dispatch(setErrorInfo({ errorMsg: err.error_message }));
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenFleetStatistics = () => {
    history.push(ROUTE.fleetManagement, {
      fromVehicleDetails: true,
      targetReport: 'statistics',
      targetVehicleId: vehicleDetails.id,
      targetRegistration: vehicleDetails.registration_num
    });
  };

  if (loading || updating || !vehicleDetails) return <Loader />;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NewVehicle
        open={openModal}
        initialValues={vehicleDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 140px)'
        }}
      >
        {/* HEADER - Same style as Site Details */}
        <CardHeader
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper'
          }}
          title={(
            <Box>
              <Typography variant="h6" fontWeight={700}>
                VEHICLE DETAILS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage vehicle configuration
              </Typography>
            </Box>
          )}
          action={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => history.replace(ROUTE.vehicles)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Back
              </Button>
            </Box>
          )}
        />

        {/* CONTENT - Flat grid like Site Details */}
        <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            {[
              { key: 'registration_num', label: 'Registration Number', value: vehicleDetails.registration_num },
              { key: 'model', label: 'Vehicle Model', value: vehicleDetails.model },
              { key: 'asset_code', label: 'Asset Code', value: vehicleDetails.asset_code },
              { key: 'group_name', label: 'Vehicle Group', value: vehicleDetails.group_name },
              { key: 'contractor_name', label: 'Contractor', value: vehicleDetails.contractor_name || vehicleDetails.contractor_display_name || vehicleDetails.contractor_label || vehicleDetails.contractor },
              { key: 'site_name', label: 'Site Name', value: vehicleDetails.site_name },
              { key: 'po_number', label: 'PO Number', value: vehicleDetails.po_number },
              { key: 'mileage_type', label: 'Mileage Type', value: getMileageType(vehicleDetails.type) },
              { key: 'standard_mileage', label: 'Standard Mileage', value: vehicleDetails.standard_mileage },
              { key: 'fleet_service_provider', label: 'Fleet Service Provider', value: vehicleDetails.fleet_service_provider },
              { key: 'created_at', label: 'Created On', value: getFormattedDate(vehicleDetails.created_at) },
              { key: 'updated_at', label: 'Last Updated', value: getFormattedDate(vehicleDetails.updated_at) }
            ].map((field) => (
              <Grid item xs={12} sm={6} md={4} key={field.key}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.02)
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      mb: 0.5
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {formatValue(field.key, field.value)}
                  </Typography>
                </Box>
              </Grid>
            ))}

            <Grid item xs={12} sm={6} md={4} key="active">
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    STATUS
                  </Typography>
                  <Typography
                    fontWeight={600}
                    color={vehicleDetails.active ? 'success.main' : 'error.main'}
                  >
                    {vehicleDetails.active ? 'Active' : 'Inactive'}
                  </Typography>
                </Box>

                <Switch
                  checked={vehicleDetails.active}
                  onChange={() => hasAdminAccess && setShowConfirm(true)}
                  disabled={!hasAdminAccess}
                  color="success"
                />
              </Box>
            </Grid>

            {Object.entries(vehicleDetails || {})
              .filter(([key]) => ![
                'registration_num',
                'model',
                'asset_code',
                'group_name',
                'contractor',
                'contractor_name',
                'contractor_display_name',
                'contractor_label',
                'site_name',
                'po_number',
                'fleet_service_provider',
                'standard_mileage',
                'created_at',
                'updated_at',
                'active'
              ].includes(key) && shouldDisplayDetailField(key))
              .map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      height: '100%',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                      }
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5
                      }}
                    >
                      {formatLabel(key)}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {formatValue(key, value)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
          </Grid>
        </CardContent>

        {(hasAdminAccess || canOpenFleetStatistics) && (
          <>
            <Divider />
            <CardActions sx={{
              justifyContent: 'flex-end', p: 3, position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
            }}
            >
              {canOpenFleetStatistics && (
                <Button
                  variant="contained"
                  onClick={handleOpenFleetStatistics}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3
                  }}
                >
                  Fleet Statistics
                </Button>
              )}
              {hasAdminAccess && (
                <Button
                  variant="contained"
                  onClick={() => setOpenModal(true)}
                  startIcon={<EditIcon />}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4
                  }}
                >
                  Edit Vehicle Details
                </Button>
              )}
            </CardActions>
          </>
        )}
      </Card>

      <ConfirmationDialog
        open={showConfirm}
        info={
          vehicleDetails.active
            ? CONFIRMATION_MSG.disableVehicle
            : CONFIRMATION_MSG.enableVehicle
        }
        disable={vehicleDetails.active}
        onClose={() => setShowConfirm(false)}
        onSubmit={toggleStatus}
      />
    </Container>
  );
};

export default VehicleDetails;
